import { AODA } from "@core/utilities/aoda";
import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { BaseComponent } from "@shared/components/base.component";
import { FormGroup } from "@angular/forms";
import { startWith, takeWhile, switchMap, exhaustMap, tap, filter } from "rxjs/operators";
import { ElasticsearchQueryService } from "@shared/services/elasticsearch-query.service";
import { Utils } from "@core/utilities/utils";
import { Observable, of, Subject } from "rxjs";

@Component({
  selector: "app-commodity-filter",
  templateUrl: "./commodity-filter.component.html",
  styleUrls: ["./commodity-filter.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CommodityFilter
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @Input() f: FormGroup;
  @Input() clearFilter: boolean;
  @Input() enableFilter: boolean;
  displayCommodityFilter: boolean = false;
  public height: string;
  totalFilterValues: number = 0;

  //Auto-Complete Variables
  lastViewedPage: number;
  lastFilter = "";
  commodities: any[] = [];
  selectedCommodities: any[] = new Array<any>();
  filteredCommodities: any[];
  isAllSelected = false;
  isInitialized = false;
  filteredCommodities$: Observable<any[]> | undefined;
  private nextPage$ = new Subject();

  constructor(
    private injector: Injector,
    private el: ElementRef,
    private r2: Renderer2,
    private searchFiltersService: ElasticsearchQueryService
  ) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();

    if(this.f.get("commodity").value.length > 0) {
      this.displayCommodityFilter = true;
      this.makeCommodityRequest();
    } else {
      this.displayCommodityFilter = false;
    }

    this.selectedCommodities = this.f.get("commodity").value;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    AODA.applyAllFixes(this.el, this.r2, this.languageService.languageType);
  }

  ngOnChanges() {
    //Reset the last viewed page if click outside Commodity filter area
    this.r2.listen('window', 'click',(e:Event)=>{
      this.lastViewedPage = 1;
      this.tempCommodity.setValue("");
      this.getObservableValues();
    });

    if (this.clearFilter) {
      this.unselectAll();
      this.clearFilter = false;
    }

    if (!this.enableFilter) {
      this.displayCommodityFilter = false;
    }
  }

  getCommodityList(startsWith: any, page: number): Observable<any[]> {
    const take = 10;
    const skip = page > 0 ? (page - 1) * take : 0;
    const filtered = this.commodities.filter((option) =>
      option._source.description
        .toLowerCase()
        .startsWith(startsWith.toLowerCase())
    );
    this.lastViewedPage = page;
    return of(filtered.slice(0, skip + take));
  }

  displayWith(element: any) {
    return element ? element.nts_num : null;
  }

  onScroll() {
    this.nextPage$.next(null);
  }

  //Filter Functions
  onDisplayCommodityFilter() {
    if (this.enableFilter) {
      if (!this.displayCommodityFilter && !this.isInitialized) {
        this.makeCommodityRequest();
      }
      this.displayCommodityFilter = !this.displayCommodityFilter;
    }
  }

  makeCommodityRequest() {
    this.isInitialized = true;
    this.searchFiltersService
      .getCommodity()
      .pipe(takeWhile(() => this.alive))
      .subscribe((commodities) => {
        this.totalFilterValues = commodities.hits.hits.length;
        this.commodities = this.filteredCommodities =
          Utils.sortElasticSearchFilterList(
            commodities.hits.hits,
            "description"
          );
        this.getObservableValues();
      });
  }

  getObservableValues() {
    const filter$ = this.tempCommodity.valueChanges.pipe(
      startWith(""),
      filter((q) => typeof q === "string")
    );

    this.filteredCommodities$ = filter$.pipe(
      switchMap((filter) => {
        let currentPage = this.lastViewedPage;
        return this.nextPage$.pipe(
          startWith(currentPage),
          exhaustMap((_) => this.getCommodityList(filter, currentPage)),
          tap(() => currentPage++)
        );
      })
    );
  }
  //Filter Functions

  isCommodityChecked(item: any) {
    if (this.isSelected(item)) {
      item.selected = true;
      return true;
    }
    item.selected = false;
    return false;
  }

  isSelected(item: any): boolean {
    return this.commodity.value.includes(item._source.description);
  }

  updateAllSelected() {
    return (this.isAllSelected =
      this.commodity.value.length == this.totalFilterValues ? true : false);
  }

  //Auto-Complete
  toggleSelectAll(event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      this.isAllSelected = !this.isAllSelected;
      this.selectedCommodities = [];

      for (let i = 0; i < this.commodities.length; i++) {
        this.commodities[i].selected = this.isAllSelected;

        if (this.isAllSelected) {
          this.selectedCommodities.push(
            this.commodities[i]._source.description
          );
        }
      }
      this.updateCommodity(this.selectedCommodities);
    }
  }

  unselectAll() {
    for (let i = 0; i < this.commodities.length; i++) {
      this.commodities[i].selected = false;
    }
    this.selectedCommodities = [];
    this.updateCommodity(this.selectedCommodities);
  }

  toggleSelection(item: any, event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      item.selected = !item.selected;
      if (item.selected && !this.isSelected(item)) {
        this.selectedCommodities.push(item._source.description);
      } else {
        const i = this.selectedCommodities.findIndex(
          (value) => value === item._source.description
        );
        this.selectedCommodities.splice(i, 1);
      }
      this.updateCommodity(this.selectedCommodities);
    }
  }

  updateCommodity(commodityList: any[]) {
    var commodities = [];
    for (var i = 0; i < commodityList.length; i++) {
      commodities.push(commodityList[i]);
    }
    this.commodity.setValue(commodities);
  }
  //Auto-Complete

  get commodity() {
    return this.f.get("commodity");
  }

  get tempCommodity() {
    return this.f.get("tempCommodity");
  }
}
